<script setup lang="ts">
import AddFileButton from "@/components/files/AddFileButton.vue";
import { useScreen } from "@/composable/useResponsive";
import ArchivesDropdownButton from "../ArchivesDropdownButton.vue";
import DrawingItem from "../../files/DrawingList/DrawingItem.vue";
import { useGetDrawingsByProject } from "@/stores/queries";
import type { DRDrawing, PaginationParams } from "@/types";
import FilesOptions from "./FilesOptions.vue";
import { computed, ref, toRef, watch } from "vue";
import DRDisplayList from "@/components/UI/items/DRDisplayList.vue";
import { useSelect } from "@/composable/useSelect";
import type { UserProperties } from "@/APIClient";
import DrawingUploader from "@/components/files/DrawingUploader.vue";
import ProjectPapper from "../ProjectPapper.vue";
import { useDrawingStore } from "@/stores/fileStore";
import UserState from "@/stores/UserState";
import { get } from "lodash";
import DRToggleButton from "@/components/UI/DRToggleButton.vue";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { useFocusOnDrawing } from "./useFocusOnDrawing";

const props = defineProps<{
  projectId: number;
  readonly: boolean;
  projectColor?: string;
}>();

const selectedUser = ref<UserProperties | null>(null);

const { screen } = useScreen();

import { useTranslation } from "i18next-vue";
const { t } = useTranslation();
const drawingStore = useDrawingStore();

const localIds = computed<number[]>(() => {
  return drawingStore.localDrawings?.map((ld) => ld.id) ?? [];
});

const projectId = toRef(props, "projectId");

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 250,
  sortBy: "createdAt",
  order: "desc",
});

const search = ref<string>("");

const { data, localDrawings, isLoading } = useGetDrawingsByProject(
  projectId,
  pagination,
  search
);

const drawings = computed<DRDrawing[]>(() => {
  return data.value?.drawings ?? localDrawings.value ?? [];
});

interface DrawingsFilters {
  sortBy: string;
  sortOrder: "asc" | "desc";
  mineOnly: boolean;
  localOnly: boolean;
}

const loadFilters = (): DrawingsFilters => {
  const saved = $ls.getItem(LocalStorageKey.DRAWINGS_FILTERS);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return {
        sortBy: "lastRevision.savedAt",
        sortOrder: "desc",
        mineOnly: false,
        localOnly: false,
      };
    }
  }
  return {
    sortBy: "lastRevision.savedAt",
    sortOrder: "desc",
    mineOnly: false,
    localOnly: false,
  };
};

const savedFilters = loadFilters();

const sortBy = ref<string>(savedFilters.sortBy);
const sortOrder = ref<"asc" | "desc">(savedFilters.sortOrder);

const mineOnly = ref(savedFilters.mineOnly);
const localOnly = ref(savedFilters.localOnly);

watch([sortBy, sortOrder, mineOnly, localOnly], () => {
  const filters: DrawingsFilters = {
    sortBy: sortBy.value,
    sortOrder: sortOrder.value,
    mineOnly: mineOnly.value,
    localOnly: localOnly.value,
  };
  $ls.setItem(LocalStorageKey.DRAWINGS_FILTERS, JSON.stringify(filters));
});

const toggleAll = () => {
  mineOnly.value = false;
  localOnly.value = false;
};

const toggleMine = () => {
  mineOnly.value = !mineOnly.value;
};

const toggleLocal = () => {
  localOnly.value = !localOnly.value;
};

const filteredDrawings = computed<DRDrawing[]>(() => {
  let result = [...drawings.value];

  if (mineOnly.value && UserState.data?.id) {
    result = result.filter(
      (drawing) => drawing.createdBy?.id === UserState.data?.id
    );
  }

  if (localOnly.value) {
    result = result.filter((drawing) => localIds.value.includes(drawing.id));
  }

  result.sort((a, b) => {
    const aValue = get(a, sortBy.value);
    const bValue = get(b, sortBy.value);

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    let comparison = 0;
    if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.localeCompare(bValue);
    } else if (aValue instanceof Date && bValue instanceof Date) {
      comparison = aValue.getTime() - bValue.getTime();
    } else if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return sortOrder.value === "asc" ? comparison : -comparison;
  });

  return result;
});

const drawingIds = computed(() => {
  return filteredDrawings.value.map((dwg) => dwg.id);
});

const { selectedIds, selectMode, setSelectedIds, toggleSelect } =
  useSelect(drawingIds);

const { keepFocusOn } = useFocusOnDrawing(filteredDrawings);
</script>

<template>
  <div :key="projectId">
    <ProjectPapper :projectColor="projectColor" class="my-3">
      <template #top-left>
        <span class="h5">{{ t("dashboard:project.sections.drawings") }}</span>
      </template>
      <template #top-right v-if="!selectedUser">
        <ArchivesDropdownButton v-if="!readonly" :projectId="projectId" />
        <AddFileButton :mini="screen.lt.sm" :projectId="projectId" />
      </template>

      <div
        v-if="drawings?.length"
        class="filter-bar my-3 d-flex flex-wrap gap-2 align-items-center"
      >
        <div class="d-flex gap-2 flex-wrap">
          <DRToggleButton
            :active="!mineOnly && !localOnly"
            :label="$t('dashboard:filters.all')"
            size="small"
            @click="toggleAll"
          />

          <DRToggleButton
            :active="mineOnly"
            icon="bi-person"
            :label="$t('dashboard:filters.myPlans')"
            size="small"
            @click="toggleMine"
          />

          <DRToggleButton
            :active="localOnly"
            icon="bi-wifi-off"
            :label="$t('dashboard:filters.availableOffline')"
            size="small"
            @click="toggleLocal"
          />
        </div>

        <div class="flex-grow-1"></div>

        <div class="d-flex gap-2 align-items-center flex-wrap">
          <el-select
            v-model="sortBy"
            :placeholder="$t('dashboard:filters.sortBy')"
            style="width: 200px"
          >
            <el-option
              value="name"
              :label="$t('dashboard:projects.list.sortBy.name')"
            />
            <el-option
              value="lastRevision.savedAt"
              :label="$t('dashboard:projects.list.sortBy.lastSave')"
            />
            <el-option
              value="createdBy.full_name"
              :label="$t('dashboard:filters.author')"
            />

            <template #prefix>
              <el-icon size="1.5rem">
                <i class="bi-sort-alpha-down"></i>
              </el-icon>
            </template>
          </el-select>

          <button
            class="sort-button"
            @click="sortOrder = sortOrder === 'asc' ? 'desc' : 'asc'"
          >
            <i :class="sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down'"></i>
          </button>
        </div>
      </div>

      <div class="d-flex align-items-center pb-3">
        <FilesOptions
          :admin-mode="false"
          :selectedIds="selectedIds"
          :drawingIds="drawingIds"
          :projectId="projectId"
          @select="setSelectedIds"
          v-model:selectMode="selectMode"
        />
      </div>

      <DRDisplayList
        v-if="filteredDrawings?.length"
        :data="filteredDrawings"
        key-index="id"
        :min-width="250"
      >
        <template #item="{ item, display }">
          <DrawingItem
            :drawing="item"
            :select-mode="selectMode"
            :selected="selectedIds.includes(item.id)"
            @toggle-select="toggleSelect(item.id)"
            :display="display"
            @keep-focus-on="keepFocusOn(item.id)"
            :data-drawing-id="item.id"
          />
        </template>
      </DRDisplayList>

      <div
        v-else-if="!isLoading && !filteredDrawings?.length && drawings?.length"
      >
        <div class="container-fluid py-3">
          <el-empty :description="$t('dashboard:filters.noResults')">
          </el-empty>
        </div>
      </div>

      <div v-else-if="!isLoading">
        <div class="container-fluid py-3">
          <el-empty description=" "> </el-empty>
          <p v-if="!readonly" class="fw-normal text-center h5 text-grey">
            {{ $t("dashboard:drag.n.drop.files.here.dwg") }}
          </p>
        </div>
      </div>
    </ProjectPapper>
  </div>

  <DrawingUploader v-if="!readonly && projectId" :projectId="projectId" />
</template>

<style scoped lang="scss">
.filter-bar {
  @media (max-width: 768px) {
    .flex-grow-1 {
      flex-basis: 100%;
      height: 0;
    }
  }
}

.sort-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background-color: transparent;
  color: var(--bs-body-color);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--bs-secondary-bg);
    border-color: var(--bs-border-color-translucent);
  }

  &:active {
    transform: scale(0.95);
  }

  i {
    font-size: 1.125rem;
  }
}
</style>
